import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Task } from '../../../types/task';
import { format, startOfDay, addDays, subDays, isToday, isSameDay } from 'date-fns';
import { fetchApi } from '../../../lib/apiClient';
import { updateTaskAtom } from '../../../atoms/v0/taskAtoms';
import { useToast } from '@chakra-ui/react';

interface HabitCardProps {
  task: Task;
}

// 简单的内存缓存，避免重复请求相同的 recurrenceGroupId
interface CacheEntry {
  data: Task[];
  timestamp: number;
}

const habitTasksCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

export const HabitCard: React.FC<HabitCardProps> = ({ task }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [habitTasks, setHabitTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [, updateTask] = useAtom(updateTaskAtom);

  // 生成过去7天和未来7天的日期网格（共15天）
  const dateGrid = useMemo(() => {
    const today = startOfDay(new Date());
    const dates = [];

    // 过去7天
    for (let i = 7; i >= 1; i--) {
      dates.push(subDays(today, i));
    }

    // 今天
    dates.push(today);

    // 未来7天
    for (let i = 1; i <= 7; i++) {
      dates.push(addDays(today, i));
    }

    return dates;
  }, []);

  // 获取同一习惯组的所有任务（带缓存）
  useEffect(() => {
    const fetchHabitTasks = async () => {
      if (!task.recurrenceGroupId) {
        setLoading(false);
        return;
      }

      // 检查缓存
      const cacheKey = task.recurrenceGroupId;
      const cached = habitTasksCache.get(cacheKey);
      const now = Date.now();

      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // 使用缓存数据
        setHabitTasks(cached.data);
        setLoading(false);
        return;
      }

      try {
        // 查询同一 recurrenceGroupId 的所有已完成任务
        const params = new URLSearchParams({
          recurrenceGroupId: task.recurrenceGroupId,
          done: 'true', // 只查询已完成的任务
          limit: '100', // 获取足够多的历史记录
        });

        const response = await fetchApi<Task[]>(`/task/list?${params.toString()}`);
        const data = response || [];

        // 更新缓存
        habitTasksCache.set(cacheKey, {
          data,
          timestamp: now
        });

        setHabitTasks(data);
      } catch (error) {
        console.error('Failed to fetch habit tasks:', error);
        setHabitTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHabitTasks();
  }, [task.recurrenceGroupId]);

  // 根据真实任务数据获取习惯完成状态
  const getHabitStatus = (date: Date): 'completed' | 'missed' | 'future' | 'today' => {
    if (isToday(date)) return 'today';
    if (date > new Date()) return 'future';

    // 查找该日期对应的任务
    const taskForDate = habitTasks.find(habitTask => {
      if (!habitTask.doneAt) return false;
      // 将完成时间转换为本地时间的日期，确保时区一致性
      const completedDate = new Date(habitTask.doneAt);
      const completedLocalDate = startOfDay(new Date(
        completedDate.getFullYear(),
        completedDate.getMonth(),
        completedDate.getDate()
      ));
      return isSameDay(completedLocalDate, date);
    });

    return taskForDate ? 'completed' : 'missed';
  };

  // 计算真实的连续天数
  const calculateStreak = (): number => {
    if (loading || habitTasks.length === 0) return 0;

    const today = startOfDay(new Date());
    let streak = 0;
    let currentDate = subDays(today, 1); // 从昨天开始计算，今天的状态不影响连续天数

    // 从昨天开始往前计算连续天数
    while (true) {
      const taskForDate = habitTasks.find(habitTask => {
        if (!habitTask.doneAt) return false;
        // 将完成时间转换为本地时间的日期，确保时区一致性
        const completedDate = new Date(habitTask.doneAt);
        const completedLocalDate = startOfDay(new Date(
          completedDate.getFullYear(),
          completedDate.getMonth(),
          completedDate.getDate()
        ));
        return isSameDay(completedLocalDate, currentDate);
      });

      if (taskForDate) {
        streak++;
        currentDate = subDays(currentDate, 1);
      } else {
        // 如果某天没完成，连续天数中断
        break;
      }
    }

    // 如果今天已经完成，连续天数+1
    const todayCompleted = habitTasks.some(habitTask => {
      if (!habitTask.doneAt) return false;
      // 将完成时间转换为本地时间的日期，确保时区一致性
      const completedDate = new Date(habitTask.doneAt);
      const completedLocalDate = startOfDay(new Date(
        completedDate.getFullYear(),
        completedDate.getMonth(),
        completedDate.getDate()
      ));
      return isSameDay(completedLocalDate, today);
    });

    if (todayCompleted) {
      streak++;
    }

    return streak;
  };

  // 计算真实的完成率（过去7天 + 今天）
  const calculateCompletionRate = (): number => {
    if (loading) return 0;

    const today = startOfDay(new Date());
    const pastSevenDays = dateGrid.slice(0, 8); // 过去7天 + 今天

    // 只计算已经过去的日期（包括今天）
    const validDays = pastSevenDays.filter(date => date <= today);
    const completedDays = validDays.filter(date => getHabitStatus(date) === 'completed').length;

    return validDays.length > 0 ? Math.round((completedDays / validDays.length) * 100) : 0;
  };

  const streak = calculateStreak();
  const completionRate = calculateCompletionRate();

  // 处理隐藏/显示习惯
  const handleToggleHabitVisibility = async () => {
    const newIsHabit = !task.isHabit;
    const actionText = newIsHabit ? '显示习惯' : '隐藏习惯';

    try {
      const success = await updateTask({
        id: task.id,
        isHabit: newIsHabit,
      });

      if (success) {
        toast({
          title: `${actionText}成功`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top'
        });
      } else {
        throw new Error('更新失败');
      }
    } catch (error) {
      console.error('切换习惯显示状态失败:', error);
      toast({
        title: `${actionText}失败`,
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-ios-purple/5 to-ios-pink/5 dark:from-ios-dark-purple/10 dark:to-ios-dark-pink/10 rounded-ios-xl p-4 border border-ios-purple/10 dark:border-ios-dark-purple/20">
        <div className="flex items-center justify-center h-32">
          <div className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">
            {t('v3/common:loading.loadingHabitData', '加载习惯数据中...')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-ios-purple/5 to-ios-pink/5 dark:from-ios-dark-purple/10 dark:to-ios-dark-pink/10 rounded-ios-xl p-4 border border-ios-purple/10 dark:border-ios-dark-purple/20 relative">
      {/* 隐藏/显示习惯按钮 */}
      <button
        onClick={handleToggleHabitVisibility}
        className="absolute top-3 right-3 flex items-center space-x-1 px-2 py-1 rounded-ios-md hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray5 transition-colors duration-200"
        title={task.isHabit ? "隐藏习惯" : "显示习惯"}
      >
        {task.isHabit ? (
          <>
            {/* Archive 图标 */}
            <svg className="w-3 h-3 text-ios-gray dark:text-ios-dark-gray" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4H21L20 6H4L3 4ZM4 8H20V19C20 19.5523 19.5523 20 19 20H5C4.44772 20 4 19.5523 4 19V8ZM9 10V12H15V10H9Z" fill="currentColor"/>
            </svg>
            <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">{t('v3/tasks:actions.hideHabit', '隐藏习惯')}</span>
          </>
        ) : (
          <>
            {/* Unarchive 图标 */}
            <svg className="w-3 h-3 text-ios-gray dark:text-ios-dark-gray" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4H21L20 6H4L3 4ZM4 8H20V19C20 19.5523 19.5523 20 19 20H5C4.44772 20 4 19.5523 4 19V8ZM9 10V12H15V10H9Z" fill="currentColor"/>
              <path d="M12 11L15 14H13V17H11V14H9L12 11Z" fill="currentColor"/>
            </svg>
            <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">{t('v3/tasks:actions.showHabit', '显示习惯')}</span>
          </>
        )}
      </button>

      {/* 习惯标题 */}
      <div className="flex items-center mb-4 pr-20">
        <div className="w-3 h-3 rounded-full bg-ios-purple dark:bg-ios-dark-purple mr-2"></div>
        <h4 className="text-ios-body font-semibold text-ios-black dark:text-white">
          {task.title}
        </h4>
      </div>

      {/* 打卡网格 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">
              {t('v3/tasks:habits.checkInRecord', '打卡记录')}
            </span>
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-sm bg-ios-green dark:bg-ios-dark-green mr-1"></div>
                <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">{t('v0/common:common.completed', '已完成')}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-sm bg-ios-purple dark:bg-ios-dark-purple mr-1"></div>
                <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">{t('v3/tasks:habits.today', '今天')}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-sm bg-ios-gray5 dark:bg-ios-dark-gray5 border border-dashed border-ios-gray3 dark:border-ios-dark-gray3 mr-1"></div>
                <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">{t('v3/tasks:habits.future', '未来')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1 overflow-x-auto">
            {dateGrid.map((date) => {
              const status = getHabitStatus(date);
              const isCurrentDay = isToday(date);

              return (
                <div
                  key={date.toISOString()}
                  className={`
                    w-4 h-4 rounded-sm transition-all duration-200 relative flex-shrink-0
                    ${status === 'completed'
                      ? 'bg-ios-green dark:bg-ios-dark-green shadow-sm'
                      : status === 'missed'
                      ? 'bg-ios-gray4 dark:bg-ios-dark-gray4'
                      : status === 'today'
                      ? 'bg-ios-purple dark:bg-ios-dark-purple ring-2 ring-ios-purple/30 dark:ring-ios-dark-purple/30'
                      : 'bg-ios-gray5 dark:bg-ios-dark-gray5 border border-dashed border-ios-gray3 dark:border-ios-dark-gray3'
                    }
                    ${isCurrentDay ? 'scale-110' : ''}
                  `}
                  title={format(date, 'MM/dd')}
                >
                  {/* 今天的特殊标记 */}
                  {isCurrentDay && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-ios-orange dark:bg-ios-dark-orange rounded-full"></div>
                  )}

                  {/* 完成状态图标 */}
                  {status === 'completed' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center space-x-4 ml-4">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-ios-orange dark:text-ios-dark-orange mr-1" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L9.5 5.5L14 7L9.5 8.5L8 13L6.5 8.5L2 7L6.5 5.5L8 1Z" fill="currentColor"/>
              </svg>
              <span className="text-ios-footnote font-medium text-ios-orange dark:text-ios-dark-orange">
                {t('v3/tasks:habits.streakDays', '{{count}}天连续', { count: streak })}
              </span>
            </div>

            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-ios-green dark:bg-ios-dark-green mr-1 flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="text-ios-footnote font-medium text-ios-green dark:text-ios-dark-green">
                {completionRate}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 激励文案 */}
      <div className="mt-4 p-3 bg-white/50 dark:bg-ios-dark-gray6/50 rounded-ios-lg">
        <p className="text-ios-caption text-center text-ios-gray dark:text-ios-dark-gray">
          {streak >= 7
            ? t('v3/me:habits.encouragement.excellent', `🔥 太棒了！你已经坚持了${streak}天，继续保持！`, { streak })
            : streak >= 3
            ? t('v3/me:habits.encouragement.good', `💪 很好！再坚持${7-streak}天就能达到一周目标`, { remaining: 7-streak })
            : t('v3/me:habits.encouragement.start', `🌱 刚刚开始，每一天都是进步！`)
          }
        </p>
      </div>
    </div>
  );
};
